// Pure health rule for background jobs (spec §6). No imports: the server and a future alert cron share it.

export type JobRunStatus = 'running' | 'ok' | 'failed' | 'skipped';
export type JobRunRow = {
  job: string; source: string; execution_id: string | null; task_index: number | null; task_count: number | null;
  started_at: string; finished_at: string | null; status: JobRunStatus; counts: Record<string, unknown>; error: string | null;
};
export type Health = 'green' | 'amber' | 'red' | 'off' | 'paused';
export type JobKey = 'tick' | 'email-sync' | 'bill-run-slice' | 'email-rematch' | 'parse-worker' | 'report-sweep' | 'sync-gcs' | 'bill-run-nightly';
export type JobSpec = { label: string; staleAfterMs: number | null; scheduled: boolean; tickDriven: boolean };

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
const RUNNING_FALLBACK_MS = 30 * MIN;
const GCS_EXECUTION_GRACE_MS = 2 * HOUR;

export const JOB_SPECS: Record<JobKey, JobSpec> = {
  'tick': { label: 'Tick', staleAfterMs: 15 * MIN, scheduled: true, tickDriven: false },
  'email-sync': { label: 'Email sync', staleAfterMs: 15 * MIN, scheduled: true, tickDriven: true },
  'bill-run-slice': { label: 'Bill run slice', staleAfterMs: 15 * MIN, scheduled: true, tickDriven: true },
  'email-rematch': { label: 'Email rematch', staleAfterMs: 15 * MIN, scheduled: true, tickDriven: true },
  'parse-worker': { label: 'Parse worker', staleAfterMs: null, scheduled: true, tickDriven: true },
  'report-sweep': { label: 'Report sweep', staleAfterMs: 15 * MIN, scheduled: true, tickDriven: false },
  'sync-gcs': { label: 'Sync GCS', staleAfterMs: 26 * HOUR, scheduled: true, tickDriven: false },
  'bill-run-nightly': { label: 'Bill run nightly', staleAfterMs: 26 * HOUR, scheduled: false, tickDriven: false },
};

export const TICK_STEP_JOBS: readonly string[] = [
  'tick-reap', 'parse-queue-sweep', 'pause-check', 'ingest-topup', 'parse-topup', 'bill-run-dispatch', 'email-reextract', 'job-run-prune',
];

export type JobHealth = {
  job: JobKey; label: string; health: Health; lastRunAt: string | null; lastOkAt: string | null;
  lastSource: string | null; lastError: string | null; counts: Record<string, unknown>;
  recent: Array<{ startedAt: string; source: string; status: JobRunStatus; error: string | null }>;
};

export function firstLine(s: string | null): string | null {
  if (s === null) return null;
  return s.split('\n')[0].trim();
}

export function effectiveStatus(row: JobRunRow, staleAfterMs: number | null, now: number): JobRunStatus {
  if (row.status !== 'running') return row.status;
  const limit = staleAfterMs ?? RUNNING_FALLBACK_MS;
  return now - Date.parse(row.started_at) > limit ? 'failed' : 'running';
}

const isAlive = (s: JobRunStatus): boolean => s === 'ok' || s === 'skipped';

export function gcsExecutions(rows: JobRunRow[], now: number): JobRunRow[] {
  const groups = new Map<string, JobRunRow[]>();
  const out: JobRunRow[] = [];
  for (const r of rows) {
    if (r.execution_id === null) { out.push(r); continue; }
    const g = groups.get(r.execution_id);
    if (g) g.push(r); else groups.set(r.execution_id, [r]);
  }
  for (const [executionId, group] of groups) {
    const first = group[0];
    const taskCount = group.find((r) => r.task_count !== null)?.task_count ?? 1;
    const latest = new Map<number, JobRunRow>();
    for (const r of group) {
      const idx = r.task_index ?? 0;
      const cur = latest.get(idx);
      if (!cur || Date.parse(r.started_at) > Date.parse(cur.started_at)) latest.set(idx, r);
    }
    let tasksOk = 0;
    // A task with no row yet, or whose latest row is still `running` (the runner inserts one at start),
    // is pending: the execution is in progress until the grace window runs out.
    let pending = false;
    for (let i = 0; i < taskCount; i++) {
      const r = latest.get(i);
      if (!r || r.status === 'running') pending = true;
      else if (isAlive(r.status)) tasksOk++;
    }
    const earliest = group.reduce((m, r) => Math.min(m, Date.parse(r.started_at)), Infinity);
    const newestFinish = group.reduce<string | null>((m, r) => (r.finished_at && (!m || r.finished_at > m) ? r.finished_at : m), null);
    let status: JobRunStatus;
    if (tasksOk === taskCount) status = 'ok';
    else if (pending && now - earliest < GCS_EXECUTION_GRACE_MS) status = 'running';
    else status = 'failed';
    out.push({
      job: first.job, source: first.source, execution_id: executionId, task_index: null, task_count: taskCount,
      started_at: new Date(earliest).toISOString(),
      finished_at: status === 'running' ? null : newestFinish,
      status,
      counts: { tasksOk, taskCount },
      error: status === 'failed' ? `${tasksOk}/${taskCount} tasks ok` : null,
    });
  }
  return out.sort((a, b) => Date.parse(b.started_at) - Date.parse(a.started_at));
}

export function jobHealth(job: JobKey, rows: JobRunRow[], ctx: { now: number; paused: boolean }): JobHealth {
  const spec = JOB_SPECS[job];
  const runs = job === 'sync-gcs' ? gcsExecutions(rows, ctx.now) : rows;
  const withStatus = runs.map((r) => ({ r, status: effectiveStatus(r, spec.staleAfterMs, ctx.now) }));
  const latest = withStatus[0];
  const lastAlive = withStatus.find((x) => isAlive(x.status));
  const aliveInWindow = lastAlive !== undefined
    && (spec.staleAfterMs === null || ctx.now - Date.parse(lastAlive.r.started_at) <= spec.staleAfterMs);

  let health: Health;
  if (ctx.paused && spec.tickDriven) health = 'paused';
  else if (!spec.scheduled && !aliveInWindow) health = 'off';
  else if (!aliveInWindow && spec.staleAfterMs !== null) health = 'red';
  else if (latest && latest.status === 'failed') health = 'amber';
  else health = 'green';

  const failed = withStatus.find((x) => x.status === 'failed');
  return {
    job, label: spec.label, health,
    lastRunAt: latest ? latest.r.started_at : null,
    lastOkAt: lastAlive ? lastAlive.r.started_at : null,
    lastSource: latest ? latest.r.source : null,
    lastError: latest && latest.status === 'failed' ? firstLine(failed?.r.error ?? null) : null,
    counts: latest ? latest.r.counts : {},
    recent: withStatus.slice(0, 20).map((x) => ({ startedAt: x.r.started_at, source: x.r.source, status: x.status, error: firstLine(x.r.error) })),
  };
}

export function tickHealth(tick: JobHealth, latestSteps: JobRunRow[]): JobHealth {
  if (tick.health !== 'green') return tick;
  const failed = latestSteps.find((s) => s.status === 'failed');
  if (!failed) return tick;
  return { ...tick, health: 'amber', lastError: `${failed.job}: ${firstLine(failed.error) ?? 'failed'}` };
}

export function parseQueueHealth(oldestPendingAt: string | null, lastParseWorkerAt: string | null, now: number): Health {
  if (oldestPendingAt === null) return 'green';
  const age = now - Date.parse(oldestPendingAt);
  const workersAbsent = lastParseWorkerAt === null || now - Date.parse(lastParseWorkerAt) > 15 * MIN;
  if (age > HOUR && workersAbsent) return 'red';
  if (age > 15 * MIN) return 'amber';
  return 'green';
}

export function watermarkHealth(lastPolledAt: string | null, now: number): Health {
  if (lastPolledAt === null) return 'red';
  return now - Date.parse(lastPolledAt) > DAY ? 'red' : 'green';
}

export function backlogHealth(b: { pending: number; prevPending: number | null; lastRunFinishedAt: string | null; finishedSincePrev: boolean; now: number }): Health {
  if (b.pending === 0) return 'green';
  if (b.lastRunFinishedAt === null || b.now - Date.parse(b.lastRunFinishedAt) > 2 * DAY) return 'red';
  if (b.prevPending !== null && b.pending > b.prevPending && !b.finishedSincePrev) return 'amber';
  return 'green';
}

const RANK: Record<Health, number> = { red: 4, amber: 3, green: 2, paused: 1, off: 0 };

export function worst(...h: Health[]): Health {
  return h.reduce<Health>((m, x) => (RANK[x] > RANK[m] ? x : m), 'off');
}

/** GET /api/bill-validation/jobs/status (spec §7). `jobs`, `tickSteps`, `stuckLocks` and `deadWorkers` are
 *  system-wide; `extras` is the caller's org only, apart from the GCS figures, which carry no NMI list. */
export type JobsStatus = {
  paused: { paused: boolean; since: string | null };
  jobs: JobHealth[];
  tickSteps: JobRunRow[];
  stuckLocks: Array<{ job_name: string; holder: string | null; locked_at: string; expires_at: string }>;
  deadWorkers: Array<{ id: string; job_id: string; heartbeat_at: string }>;
  extras: {
    emailIntake: {
      itemsByStatus: Record<string, number>; liveWorkers: number;
      activeJob: { id: string; kind: string; state: string; started_at: string } | null;
      lastPolledAt: string | null; runs24h: { automatic: number; manual: number };
    };
    parsing: { byStatus: Record<string, number>; oldestPendingAt: string | null; stalled: number; health: Health };
    unmatched: { neverRechecked: number; rereadPending: number; rereadGaveUp: number };
    reportSends: Array<{ id: string; status: string; created_at: string; sent: number; failed: number }>;
    gcs: {
      lastExecution: { started_at: string; tasksOk: number; taskCount: number } | null;
      files: { done: number; errored: number; empty: number }; rowsInserted: number; unmatchedNmiCount: number;
    };
    billBacklog: {
      pending: number; error: number; running: number;
      lastRun: { started_at: string; finished_at: string | null; status: string; manual: boolean } | null;
    };
  };
};
